import p2 from 'p2';

export type PhysicsPart = p2.Body|p2.Constraint|p2.Spring;

export enum PhysicsShapeType
    {
    CIRCLE = 'circle',
    BOX = 'box',
    CAPSULE = 'capsule',
    POLYGON = 'polygon',
}
export interface PhysicsBodyData extends p2.BodyOptions
{
    x?: number,
    y?: number,
}

export interface PhysicsShapeSettings
{
    group?: number,
    mask?: number,
    material?: p2.Material,
    sensor?: boolean,
}

export interface PhysicsShapeData extends PhysicsShapeSettings
{
    type?: PhysicsShapeType | number,
    width?: number,
    height?: number,
    radius?: number,
    angle?: number,
    vertices?: number[][]
}

export class PhysicsUtils
{
    /** Create a shape from shape data, useful for translating json objs into shapes */
    public static createBody(
        data: PhysicsBodyData,
        ...shapes: PhysicsShapeData[]
    ): p2.Body
    {
        const body = new p2.Body({
            position: [data.x ?? 0, -(data.y ?? 0)],
            ...data,
        });

        for (const shape of shapes)
        {
            body.addShape(this.createShape(shape));
        }

        return body;
    }

    /** Create a shape from shape data, useful for translating json objs into shapes */
    public static createShape(data: PhysicsShapeData): p2.Shape
    {
        const type = data.type;

        if (type === PhysicsShapeType.CIRCLE || type === p2.Shape.CIRCLE)
        {
            return this.createCircle(data);
        }
        else if (type === PhysicsShapeType.BOX || type === p2.Shape.BOX)
        {
            return this.createBox(data);
        }
        else if (type === PhysicsShapeType.CAPSULE || type === p2.Shape.CAPSULE)
        {
            return this.createCapsule(data);
        }
        else if (type === PhysicsShapeType.POLYGON || type === p2.Shape.CONVEX)
        {
            return this.createPolygon(data);
        }

        throw new Error(`[PhysicsUtils] Shape type not recognised: ${type}`);
    }

    /** Shortcut for creating a box shape */
    public static createBox(data: PhysicsShapeData): p2.Box
    {
        const shape = new p2.Box({
            width: data.width,
            height: data.height,
        });

        this.setupShape(shape, data);

        return shape;
    }

    /** Helper for creating a circle shape */
    public static createCircle(data: PhysicsShapeData): p2.Circle
    {
        const shape = new p2.Circle({
            radius: data.radius,
            angle: data.angle ?? 0,
        });

        this.setupShape(shape, data);

        return shape;
    }

    /** Helper for creating a capsule shape */
    public static createCapsule(data: PhysicsShapeData): p2.Capsule
    {
        const w = data.width || 64;
        const h = data.height || 64;
        const radius = Math.min(w, h) * 0.5;
        const length = Math.max(w, h) - (radius * 2);

        const shape = new p2.Capsule({
            radius,
            length,
            // Capsule is always 'horizontal', so here we can guess anlge based on width/height, if not defined
            angle: data.angle ?? w > h ? 0 : Math.PI * 0.5,
        });

        this.setupShape(shape, data);

        return shape;
    }

    /** Helper for creating a polygon (convex) shape */
    public static createPolygon(data: PhysicsShapeData): p2.Convex
    {
        const shape = new p2.Convex({
            vertices: data.vertices,
            angle: data.angle ?? 0,
        });

        this.setupShape(shape, data);

        return shape;
    }

    /** Set shape common properties */
    public static setupShape(shape: p2.Shape, settings: PhysicsShapeSettings): void
    {
        if (settings.group !== undefined) shape.collisionGroup = settings.group;
        if (settings.mask !== undefined) shape.collisionMask = settings.mask;
        if (settings.material !== undefined) shape.material = settings.material;
        if (settings.sensor !== undefined) shape.sensor = settings.sensor;
    }

    /** Set shape common properties to all shapes in the list */
    public static setupShapes(shapes: p2.Shape[], settings: PhysicsShapeSettings): void
    {
        for (const shape of shapes) this.setupShape(shape, settings);
    }
}
